
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useGetFarmById } from '@/hooks/use-farm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Edit, ShoppingCart } from 'lucide-react';
import HarvestList from '@/components/harvests/HarvestList';
import HarvestSummary from '@/components/harvests/HarvestSummary';

const FarmDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: farm, isLoading, error } = useGetFarmById(id || '');

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-8 space-y-6">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-40 w-full" />
        </div>
      </Layout>
    );
  }

  if (error || !farm) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <div className="bg-red-50 p-4 rounded-md text-red-800">
            {error ? `Error loading farm details: ${error.message}` : 'Farm not found'}
          </div>
          <Button variant="outline" asChild className="mt-4">
            <Link to="/farms">Back to Farms</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <Button variant="outline" size="sm" asChild className="mb-2">
              <Link to="/farms">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Farms
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">{farm.farmer_name}'s Farm</h1>
            <p className="text-muted-foreground">{farm.region}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/procurements/new?sourceType=farm&sourceId=${farm.id}`}>
                <ShoppingCart className="mr-2 h-4 w-4" />
                Start Sourcing
              </Link>
            </Button>
            <Button asChild>
              <Link to={`/farms/${farm.id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Farm
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Farm Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-muted-foreground">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <span className="text-muted-foreground">Land Size:</span>
                      <span>{farm.land_size} ha</span>
                      
                      <span className="text-muted-foreground">Irrigation Type:</span>
                      <span>{farm.irrigation_type || "Not specified"}</span>
                      
                      <span className="text-muted-foreground">Main Product:</span>
                      <span>{farm.product_name}</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium text-muted-foreground">Farmer Information</h3>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <span className="text-muted-foreground">Name:</span>
                      <span>{farm.farmer_name}</span>
                      
                      <span className="text-muted-foreground">Phone:</span>
                      <span>{farm.phone_number}</span>
                      
                      <span className="text-muted-foreground">Union:</span>
                      <span>{farm.union_name || "Not affiliated"}</span>
                      
                      <span className="text-muted-foreground">Has Contract:</span>
                      <span>{farm.has_contract ? "Yes" : "No"}</span>
                    </div>
                  </div>
                </div>

                {farm.latitude && farm.longitude && (
                  <div>
                    <h3 className="font-medium text-muted-foreground">Location</h3>
                    <div className="grid grid-cols-2 gap-2 mt-2 max-w-xs">
                      <span className="text-muted-foreground">Latitude:</span>
                      <span>{farm.latitude}</span>
                      
                      <span className="text-muted-foreground">Longitude:</span>
                      <span>{farm.longitude}</span>
                      
                      <span className="text-muted-foreground">Distance from Addis:</span>
                      <span>{farm.distance_from_addis} km</span>
                    </div>
                  </div>
                )}

                {(farm.harvest_start_date || farm.harvest_end_date) && (
                  <div>
                    <h3 className="font-medium text-muted-foreground">Harvest Information</h3>
                    <div className="grid grid-cols-2 gap-2 mt-2 max-w-xs">
                      <span className="text-muted-foreground">Start Date:</span>
                      <span>{farm.harvest_start_date || "Not specified"}</span>
                      
                      <span className="text-muted-foreground">End Date:</span>
                      <span>{farm.harvest_end_date || "Not specified"}</span>
                      
                      <span className="text-muted-foreground">Est. Harvest:</span>
                      <span>{farm.estimated_harvest_kg || 0} kg</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <div>
            <HarvestSummary farmId={farm.id} estimatedHarvestKg={farm.estimated_harvest_kg} />
          </div>
        </div>

        <HarvestList farmId={farm.id} />
      </div>
    </Layout>
  );
};

export default FarmDetail;
